/** Date helpers shared by the server (digest) and the browser (filters). All date-only, local. */

export function todayIso(now: Date = new Date()): string {
  return toIso(now);
}

export function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d + days);
  return toIso(dt);
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const [y1, m1, d1] = from.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = to.split('-').map(Number) as [number, number, number];
  const a = Date.UTC(y1, m1 - 1, d1);
  const b = Date.UTC(y2, m2 - 1, d2);
  return Math.round((b - a) / 86_400_000);
}

/** Sunday of the current week, ISO weeks starting Monday (§7: "end of this week"). */
export function endOfWeek(today: string): string {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d);
  const dow = (dt.getDay() + 6) % 7; // Monday = 0
  return addDays(today, 6 - dow);
}

export function endOfMonth(today: string): string {
  const [y, m] = today.split('-').map(Number) as [number, number];
  return toIso(new Date(y, m, 0));
}

export type DueBucket = 'overdue' | 'today' | 'week' | 'later' | 'none';

export function dueBucket(date: string | null, today: string): DueBucket {
  if (!date) return 'none';
  const diff = daysBetween(today, date);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (date <= endOfWeek(today)) return 'week';
  return 'later';
}

/** Calendar date (YYYY-MM-DD) of an instant in a time zone; the server's "today" for a user (§5.5 followedUp). */
export function dateInZone(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
